import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { CreateRolePayload, CreateRoleResponse, Role, RoleListResponse, RoleResponse } from './role.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private readonly runtimeApiUrl = (window as { __APP_CONFIG__?: { apiUrl?: string } }).__APP_CONFIG__?.apiUrl;
  private readonly baseApiUrl = this.runtimeApiUrl || environment.apiUrl;
  private readonly apiUrl = `${this.baseApiUrl}/roles`;

  constructor(private http: HttpClient) { }

  getRoles(): Observable<Role[]> {
    const params = new HttpParams();
    return this.http.get<RoleListResponse>(this.apiUrl, { params }).pipe(
      map(response => response.roles.map(r => this.mapToRole(r)))
    );
  }

  createRole(role: Role): Observable<Role> {
    const payload = this.mapToCreatePayload(role);
    return this.http.post<RoleResponse>(this.apiUrl, payload).pipe(
      map(response => this.mapToRole(response.role))
    );
  }

  getRoleById(id: string): Observable<Role> {
    return this.http.get<CreateRoleResponse>(`${this.apiUrl}/${id}`).pipe(
      map(r => this.mapToRole(r))
    );
  }

  updateRole(id: string, role: Role): Observable<Role> {
    const payload = this.mapToCreatePayload(role);
    return this.http.put<RoleResponse>(`${this.apiUrl}/${id}`, payload).pipe(
      map(response => this.mapToRole(response.role))
    );
  }

  deleteRole(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  checkRoleAssignment(id: string): Observable<boolean> {
    return this.http.get<{ isAssigned: boolean; userCount: number }>(`${this.apiUrl}/${id}/roleAssignment`).pipe(
      map(response => response.isAssigned)
    );
  }

  private mapToCreatePayload(role: Role): CreateRolePayload {
    return {
      id: role.id,
      roleName: role.roleName.trim(),
      roleDescription: role.roleDescription.trim(),
      active: role.active === 'Yes',
      permissions: role.permissions,
    };
  }

  private mapToRole(apiRole: CreateRoleResponse): Role {
    if (typeof apiRole?.id !== 'string' || !apiRole.id.trim()) {
      throw new Error('The server returned a role without a valid ID. Please refresh the role list.');
    }
    return {
      id: apiRole.id,
      roleName: apiRole.roleName || '',
      roleDescription: apiRole.roleDescription || '',
      active: apiRole.active === true || apiRole.active === 'Yes' ? 'Yes' : 'No',
      permissions: apiRole.permissions || {
        reports: { view: false },
        knowledgeBase: { view: false, manage: false },
      },
    };
  }
}
